import getYear from 'date-fns/getYear'
import { cloneDeep, find, map, uniq, without } from 'lodash'
import React, { FC, useEffect, useState } from 'react'

import {
  OrganisationRoleEnum,
  UserModelAsAdminQueriesFilterEnum,
  UserModelAsAdminQueriesFilterInput
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { apolloClientPrivate } from '../../graphql/apolloClient'
import { createGroupMutation } from '../../operations/groupCreate'
import { RemoveUserDocument } from './components/ArchiveUserModal.codegen'
import { useManageUsersPageQuery } from './ManageUsersPage.codegen'
import {
  eachTargetId,
  getDefaultContext,
  ManageUsersPageContextType,
  ManageUsersPageContext,
  TargetUserIds
} from './ManageUsersPageContext'
import changeRoleMutation from './operations/changeRole'
import removeFromGroupMutation from './operations/removeFromGroup'
import IFilterParams from './types/IFilterParams'
import IUser from './types/IUser'
import { useBatchAddUsersToGroupMutation } from './usersAddToGroupBatch.codegen'
import { NumberParam, useQueryParam, withDefault } from 'use-query-params'

export const PAGE_SIZE = 30

export const ManageUsersPageContainer: FC = ({ children }) => {
  const defaultState = getDefaultContext()
  const [page, setPage] = useQueryParam(
    'page',
    withDefault(NumberParam, defaultState.page)
  )
  const [batchUsersToGroup] = useBatchAddUsersToGroupMutation()
  const [state, setState] = useState<ManageUsersPageContextType>({
    ...defaultState
  })

  const changeState = (newStateProps: Partial<ManageUsersPageContextType>) => {
    setState({ ...state, ...newStateProps })
  }

  useEffect(() => {
    changeState({ page })
  }, [page])

  const filterAsDescriptors = (
    isExport?
  ): UserModelAsAdminQueriesFilterInput[] => {
    const {
      filter: { groupId, role, search, showArchived }
    } = state

    const filters: UserModelAsAdminQueriesFilterInput[] = []

    if (groupId) {
      filters.push({
        name: UserModelAsAdminQueriesFilterEnum.group_id,
        value: groupId
      })
    }
    if (role) {
      filters.push({
        name: UserModelAsAdminQueriesFilterEnum.organisation_role,
        value: role
      })
    }
    if (search) {
      filters.push({
        name: UserModelAsAdminQueriesFilterEnum.search,
        value: search.trim()
      })
    }
    filters.push({
      name: UserModelAsAdminQueriesFilterEnum.is_archived,
      value: showArchived
    })

    if (isExport) {
      filters.push({
        name: UserModelAsAdminQueriesFilterEnum.groupsAllConcatenated
      })
    }

    return filters
  }

  const { loading, refetch } = useManageUsersPageQuery({
    fetchPolicy: 'network-only',
    variables: {
      filters: filterAsDescriptors(),
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      year: getYear(new Date())
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      changeState({
        users: data?.admin?.organisation?.users,
        usersCount: data?.admin?.organisation?.usersCount as number,
        selectedUserIds: []
      })
    }
  })

  const onAddToGroup = async (variables: {
    target: TargetUserIds
    groupId?: number
    groupName?: string
  }): Promise<void> => {
    const { target, groupId, groupName } = variables

    let effectiveGroupId = groupId

    if (!effectiveGroupId) {
      const newGroup = await createGroupMutation({
        name: groupName as string
      })

      effectiveGroupId = newGroup.id
    }

    const selectedUserIds = Array.isArray(target) ? target : [target]

    const userIdsNotYetInTheGroup = state.users
      .filter((user) => selectedUserIds.includes(user.id)) // Out of all users on the page, pick only selected users
      .filter(
        // Pick only users who does not have effectiveGroupId assigned yet.
        (user) => !user.groups.find((group) => group.id === effectiveGroupId)
      )

    if (!userIdsNotYetInTheGroup.length) {
      return
    }

    await batchUsersToGroup({
      variables: {
        groupId: effectiveGroupId,
        userIds: target
      }
    })

    await refetch()
  }

  const onRemoveFromGroup = async (options: {
    target: TargetUserIds
    groupId: number
  }) => {
    const { target, groupId } = options

    await eachTargetId(target, async (userId) => {
      const variables = { userId, groupId }
      const groups = await removeFromGroupMutation(variables)
      patchStoredUser(userId, { groups })
    })
  }

  const onRoleChange = async (options: {
    target: TargetUserIds
    role: OrganisationRoleEnum
  }) => {
    const { target, role } = options

    await eachTargetId(target, async (userId) => {
      const variables = { userId, role }
      const { organisation_role } = await changeRoleMutation(variables)
      patchStoredUser(userId, { organisation_role })
    })

    await refetch()
  }

  const onRemoveUser = async ({ target }: { target: TargetUserIds }) => {
    changeState({ loading: true })

    await eachTargetId(target, async (userId) =>
      apolloClientPrivate.mutate({
        mutation: RemoveUserDocument,
        variables: { userId }
      })
    )

    await refetch()
  }

  const onFilterChange = (filterChange: Partial<IFilterParams>) => {
    const { filter: oldFilter } = state
    const filter = { ...oldFilter, ...filterChange }
    changeState({ filter })
    setPage(1)
  }

  const onPageChange = (page: number) => {
    setPage(page)
  }

  const toggleUserSelection = (options: { value: boolean; userId: number }) => {
    const { selectedUserIds } = state
    const { value, userId } = options

    if (value) {
      changeState({
        selectedUserIds: uniq([...selectedUserIds, userId])
      })
    } else {
      changeState({
        selectedUserIds: without(selectedUserIds, userId)
      })
    }
  }

  const toggleAllUsersSelection = ({ value }: { value: boolean }) => {
    const { users } = state

    if (value) {
      changeState({
        selectedUserIds: map(users, ({ id }) => id)
      })
    } else {
      changeState({
        selectedUserIds: []
      })
    }
  }

  const patchStoredUser = (userId: number, data: Partial<IUser>) => {
    const { users } = state
    const newUsers = [...users]
    const originalUser = find(newUsers, ({ id }) => id === userId)
    if (originalUser) {
      const idx = newUsers.indexOf(originalUser)
      newUsers[idx] = { ...cloneDeep(originalUser), ...data }
      changeState({ users: newUsers })
    }
  }

  return (
    <ManageUsersPageContext.Provider
      value={{
        ...state,
        loading,
        toggleAllUsersSelection,
        toggleUserSelection,
        onPageChange,
        onFilterChange,
        onRemoveUser,
        onRoleChange,
        onRemoveFromGroup,
        onAddToGroup,
        filterAsDescriptors,
        fetchUsers: refetch
      }}
    >
      {children}
    </ManageUsersPageContext.Provider>
  )
}

export default ManageUsersPageContainer
